export const generateNumberWithPrecision = (
  min: number,
  max: number,
  precision: number
): number => roundNumberToPrecision(generateRandomNumber(min, max), precision)

export function generateRandomNumber(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

export function roundNumberToPrecision(number, precision): number {
  const power = Math.pow(10, precision)

  return Math.floor(number * power) / power
}

/**
 * Formats numbers in an easy-to-read way for GAM GAM tracking.
 *
 * Example formats:
 * input  | output
 * '30.34'| 30.34
 * .03    | 0.03
 * 3      | 3.00
 * 34     | 34.00
 * 34.3   | 34.30
 * 340.3  | 340.30
 */
export function formatFloor(number: number | string | undefined): string {
  if (typeof number === 'string') {
    return formatFloor(parseFloat(number))
  }

  if (typeof number === 'undefined') {
    return '-1'
  }

  if (number > 2) {
    return '2+'
  }

  const toHundreds = 1
  const toPenny = 2
  const toString = number.toFixed(2)
  let [beforeDecimal, afterDecimal] = toString.split('.')

  beforeDecimal = beforeDecimal || ''
  afterDecimal = afterDecimal || ''

  while (beforeDecimal.length < toHundreds) {
    beforeDecimal = `0${beforeDecimal}`
  }

  while (afterDecimal.length < toPenny) {
    afterDecimal += '0'
  }

  return `${beforeDecimal}.${afterDecimal}`
}

/**
 * GAM contains Line Items of of differing density as the numbers get higher. However, bids
 * come in at a higher precision than those. This function transforms the received bid
 * into one that will match against a Line Item in DFP.
 * @param bidCpm
 */
export function bucketizeBid(bidCpm: number): number {
  /* eslint-disable no-magic-numbers */
  const bid = Math.round(bidCpm * 100) / 100.0
  let bucketedBid = 0

  if (bid <= 0.1) {
    bucketedBid = 0.1
  } else if (bid < 1.11) {
    bucketedBid = 0.01 * Math.round(bid / 0.01)
  } else if (bid < 1.8) {
    bucketedBid = 0.03 * Math.round(bid / 0.03)
  } else if (bid < 3.0) {
    bucketedBid = 0.05 * Math.round(bid / 0.05)
  } else if (bid < 12.0) {
    bucketedBid = 0.15 * Math.round(bid / 0.15)
  } else if (bid < 20.0) {
    bucketedBid = 0.25 * Math.round(bid / 0.25)
  } else if (bid < 40.0) {
    bucketedBid = 1.0 * Math.round(bid / 1.0)
  } else if (bid < 70.0) {
    bucketedBid = 2.0 * Math.round(bid / 2.0)
  } else if (bid < 200.0) {
    bucketedBid = 5.0 * Math.round(bid / 5.0)
  } else {
    bucketedBid = 200
  }

  return bucketedBid
  /* eslint-enable no-magic-numbers */
}

export function calculateUrTargeting(bidCpm: number | string): string {
  const bidFloat = Number(bidCpm)
  const newBid = bucketizeBid(bidFloat)

  const ur = Math.round(newBid * 100)

  return ur.toString()
}
